import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Paper } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useDispatch } from "react-redux";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import {
  setFormOpen,
  setSelectedJobCategoryID,
} from "../../redux/filmsReducer";
import { useDeleteJobCategoryMutation } from "../../redux/jobCategory/jobCategoryEndpoints";
import { useJobCategories } from "../../redux/jobCategory/jobCategoryHooks";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";

/**
 * JobCategory
 *
 * @returns {null} JobCategory
 */
export default function JobCategories() {
  const { jobCategories } = useJobCategories();

  const [deleteJobCategory] = useDeleteJobCategoryMutation();

  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        onRowClick={(e) => {
          dispatch(setSelectedJobCategoryID(e.row.id));
          dispatch(setFormOpen({ isOpen: true, formID: "jobCategory" }));
        }}
        apiRef={apiRef}
        rows={jobCategories ?? []}
        columns={jobCategoryColumns(dispatch, confirm, deleteJobCategory)}
        showToolbar
        slots={{
          toolbar: JobCategoryToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
      />
    </Paper>
  );
}

function JobCategoryToolbar() {
  const dispatch = useDispatch();

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 1,
          gap: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Button
          variant="soft"
          size="sm"
          onClick={() =>
            dispatch(setFormOpen({ formID: "jobCategory", isOpen: true }))
          }
        >
          + New Job Category
        </Button>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

const jobCategoryColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  deleteJobCategory: (id: number) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "number",
      width: 40,
    },
    {
      field: "name",
      headerName: "Name",
      editable: true,
      type: "string",
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any }) => [
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label={"Delete"}
          onClick={() => {
            confirm({
              description: `You are about to delete a jobCategory`,
            })
              .then(() => {
                deleteJobCategory(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);
